import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  downloadCsv,
  getReportExceptions,
  recordAccountReview,
  TAccountReviewCreate,
  TExceptionClass,
  TExceptionProgram,
  TExceptionReviewDue,
  TExceptionWatch,
  TReportExceptions,
  TReviewKind,
} from '../../../requests/opsReports';
import {
  btnGhost,
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  OriginBadge,
  Pill,
  tableClass,
  tdClass,
  thClass,
  theadClass,
} from '../ui';

type ListKey =
  | 'commitsWithoutReservation'
  | 'classesWithoutAssignment'
  | 'prepCallsDue'
  | 'accountReviewsDue'
  | 'watchlist'
  | 'wrapsDue'
  | 'reservationDrift'
  | 'holdsExpiring';

const LISTS: { key: ListKey; title: string; replaces: string }[] = [
  {
    key: 'commitsWithoutReservation',
    title: 'Available, no reservation',
    replaces: 'Opps in Commit with no Reservation',
  },
  {
    key: 'classesWithoutAssignment',
    title: 'Named instructor, no assignment',
    replaces: 'Reservations with no SME',
  },
  {
    key: 'prepCallsDue',
    title: 'Prep calls due',
    replaces: 'Upcoming Prep Calls Required',
  },
  {
    key: 'accountReviewsDue',
    title: 'Account reviews due',
    replaces: 'Account Review Meetings cadence',
  },
  {
    key: 'watchlist',
    title: 'Under-consumption watchlist',
    replaces: 'no report today',
  },
  {
    key: 'wrapsDue',
    title: 'Wraps due',
    replaces: 'wrap task 30 days before end',
  },
  {
    key: 'reservationDrift',
    title: 'Class and reservation out of sync',
    replaces: 'Event and Reservation Out of Sync',
  },
  {
    key: 'holdsExpiring',
    title: 'Holds running out',
    replaces: 'Hold Expired',
  },
];

const money = (n: number | null) =>
  n == null
    ? '—'
    : n.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });

const classLink = (id: string) => PATH.opsClassEdit.replace(':id', id);
const accountLink = (id: string) => PATH.opsAccountView.replace(':id', id);
const programLink = (id: string) => `${PATH.opsPrograms}?id=${id}`;

/**
 * Eight lists that are done when they are empty. The counts sit at the top
 * so a morning check is one glance; each row opens the record it is
 * about, and the review / wrap lists can be cleared from here.
 */
const Exceptions: React.FC = () => {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['opsReportExceptions'],
    queryFn: getReportExceptions,
  });

  const record = useMutation({
    mutationFn: (body: TAccountReviewCreate) => recordAccountReview(body),
    onSuccess: (r) => {
      showToast(
        r.kind === 'wrap' ? 'Wrap recorded' : 'Review recorded',
        'success',
      );
      queryClient.invalidateQueries({ queryKey: ['opsReportExceptions'] });
      queryClient.invalidateQueries({ queryKey: ['opsAccountReviews'] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const csv = async () => {
    setBusy(true);
    try {
      await downloadCsv('ops/reports/exceptions', {}, 'exceptions.csv');
    } catch (e) {
      showToast(resolveApiMessage(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-md border border-red-500 bg-red-100 px-4 py-2 text-sm text-red-one">
        {resolveApiMessage(error)}
      </div>
    );
  }

  const total = LISTS.reduce((s, l) => s + data[l.key].count, 0);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="text-sm text-gray-three">
          {total === 0
            ? 'Every list is empty.'
            : `${total.toLocaleString()} open across six lists`}{' '}
          · as of {new Date(data.generatedAt).toLocaleTimeString()}
        </div>
        <span className="flex-1" />
        <button className={btnSecondary} onClick={csv} disabled={busy}>
          {busy ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {LISTS.map((l) => (
          <a
            key={l.key}
            href={`#${l.key}`}
            className="rounded-xl border border-white-regular bg-white px-4 py-3 transition-colors hover:border-blue-primary"
          >
            <div
              className={`text-2xl font-bold [font-variant-numeric:tabular-nums] ${
                data[l.key].count === 0 ? 'text-gray-two' : 'text-gray-four'
              }`}
            >
              {data[l.key].count.toLocaleString()}
            </div>
            <div className="mt-0.5 text-xs text-gray-three">{l.title}</div>
          </a>
        ))}
      </div>

      <div className="space-y-6">
        <ClassList
          id="commitsWithoutReservation"
          meta={LISTS[0]}
          list={data.commitsWithoutReservation}
          extra={
            data.commitsWithoutReservation.portalBorn.count > 0 && (
              <div className="border-t border-white-three px-5 py-3 text-xs text-gray-three">
                Plus {data.commitsWithoutReservation.portalBorn.count} born in
                the portal, which never carry a GoMeedo reservation — listed
                apart on purpose, not as a fault.
                <ul className="mt-1 space-y-0.5">
                  {data.commitsWithoutReservation.portalBorn.items
                    .slice(0, 10)
                    .map((c) => (
                      <li key={c.id}>
                        <Link
                          to={classLink(c.id)}
                          className="text-blue-primary hover:underline"
                        >
                          {c.name}
                        </Link>{' '}
                        · {c.start?.slice(0, 10)}
                      </li>
                    ))}
                </ul>
              </div>
            )
          }
        />
        <ClassList
          id="classesWithoutAssignment"
          meta={LISTS[1]}
          list={data.classesWithoutAssignment}
          fixTo={PATH.opsAssignments}
          fixLabel="Assign"
        />
        <ClassList
          id="prepCallsDue"
          meta={LISTS[2]}
          list={data.prepCallsDue}
          showPrepStatus
        />
        <ProgramList
          id="accountReviewsDue"
          meta={LISTS[3]}
          list={data.accountReviewsDue}
          kind="review"
          onRecord={(b) => record.mutate(b)}
          saving={record.isPending}
        />
        <WatchList
          id="watchlist"
          meta={LISTS[4]}
          list={data.watchlist}
          onRecord={(b) => record.mutate(b)}
          saving={record.isPending}
        />
        <ProgramList
          id="wrapsDue"
          meta={LISTS[5]}
          list={data.wrapsDue}
          kind="wrap"
          onRecord={(b) => record.mutate(b)}
          saving={record.isPending}
        />
        <ListCard
          id="reservationDrift"
          meta={LISTS[6]}
          count={data.reservationDrift.count}
          cap={200}
          basis={
            data.reservationDrift.skipped
              ? data.reservationDrift.reason ?? 'not compared'
              : 'upcoming Salesforce-born classes beside their mirrored GoMeddo reservation; each field where the two disagree'
          }
        >
          {data.reservationDrift.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className={tableClass}>
                <thead className={theadClass}>
                  <tr>
                    <th className={thClass}>Starts</th>
                    <th className={thClass}>Class</th>
                    <th className={thClass}>Account</th>
                    <th className={thClass}>Instructor</th>
                    <th className={thClass}>Differs on</th>
                    <th className={thClass}>Stale side</th>
                  </tr>
                </thead>
                <tbody>
                  {data.reservationDrift.items.map((r) => (
                    <tr key={r.id}>
                      <td className={`${tdClass} whitespace-nowrap`}>
                        {r.start?.slice(0, 10) ?? '—'}
                      </td>
                      <td className={tdClass}>
                        <Link
                          to={classLink(r.id)}
                          className="text-blue-primary hover:underline"
                        >
                          {r.name ?? r.id}
                        </Link>
                      </td>
                      <td className={tdClass}>{r.accountName ?? '—'}</td>
                      <td className={tdClass}>{r.instructor ?? '—'}</td>
                      <td className={tdClass}>
                        {r.drift.map((d) => (
                          <div key={d.field} className="text-xs">
                            <b>{d.field}</b>: portal {d.portal ?? '—'} ·
                            reservation {d.reservation ?? '—'}
                          </div>
                        ))}
                      </td>
                      <td className={tdClass}>
                        {r.staleSide === 'reservation' ? (
                          <Pill
                            value="reservation (portal moved it)"
                            tone="warn"
                          />
                        ) : (
                          <Pill value="decide" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ListCard>
        <ListCard
          id="holdsExpiring"
          meta={LISTS[7]}
          count={data.holdsExpiring.count}
          cap={200}
          basis="classes on Hold whose expiry falls in the next 7 days; an expired hold frees the lane on the next hourly run"
        >
          {data.holdsExpiring.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className={tableClass}>
                <thead className={theadClass}>
                  <tr>
                    <th className={thClass}>Hold ends</th>
                    <th className={thClass}>Class</th>
                    <th className={thClass}>Starts</th>
                    <th className={thClass}>Account</th>
                    <th className={thClass}>Instructor</th>
                    <th className={thClass}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {data.holdsExpiring.items.map((r) => (
                    <tr key={r.id}>
                      <td className={`${tdClass} whitespace-nowrap`}>
                        {r.holdExpiresAt
                          ? String(r.holdExpiresAt)
                              .slice(0, 16)
                              .replace('T', ' ')
                          : '—'}
                        {r.expired && <Pill value="expired" tone="bad" />}
                      </td>
                      <td className={tdClass}>
                        <Link
                          to={classLink(r.id)}
                          className="text-blue-primary hover:underline"
                        >
                          {r.name ?? r.id}
                        </Link>
                      </td>
                      <td className={`${tdClass} whitespace-nowrap`}>
                        {r.start?.slice(0, 10) ?? '—'}
                      </td>
                      <td className={tdClass}>{r.accountName ?? '—'}</td>
                      <td className={tdClass}>{r.instructor ?? '—'}</td>
                      <td className={tdClass}>{r.note ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ListCard>
      </div>
    </>
  );
};

// ── Shared frame ───────────────────────────────────────────────────────────

const ListCard: React.FC<{
  id: string;
  meta: { title: string; replaces: string };
  count: number;
  cap: number;
  basis: string;
  children: React.ReactNode;
  extra?: React.ReactNode;
}> = ({ id, meta, count, cap, basis, children, extra }) => (
  <div id={id}>
    <Card
      title={meta.title}
      right={
        <span className="flex items-center gap-2 text-xs text-gray-three">
          <span>replaces “{meta.replaces}”</span>
          <Pill
            value={count === 0 ? 'empty' : `${count.toLocaleString()} open`}
            tone={count === 0 ? 'good' : 'warn'}
          />
        </span>
      }
    >
      <div className="border-b border-white-three px-5 py-2 text-xs text-gray-three">
        {basis}
        {count > cap && ` · showing the first ${cap}`}
      </div>
      {count === 0 ? (
        <EmptyState title="Nothing here" hint="This list is worked to empty." />
      ) : (
        children
      )}
      {extra}
    </Card>
  </div>
);

// ── Classes ────────────────────────────────────────────────────────────────

const ClassList: React.FC<{
  id: ListKey;
  meta: { title: string; replaces: string };
  list: { count: number; basis: string; items: TExceptionClass[] };
  fixTo?: string;
  fixLabel?: string;
  showPrepStatus?: boolean;
  extra?: React.ReactNode;
}> = ({ id, meta, list, fixTo, fixLabel, showPrepStatus, extra }) => (
  <ListCard
    id={id}
    meta={meta}
    count={list.count}
    cap={200}
    basis={list.basis}
    extra={extra}
  >
    <div className="overflow-x-auto">
      <table className={tableClass}>
        <thead className={theadClass}>
          <tr>
            <th className={thClass}>Starts</th>
            <th className={`${thClass} text-right`}>In</th>
            <th className={thClass}>Class</th>
            <th className={thClass}>Type</th>
            <th className={thClass}>Account</th>
            <th className={thClass}>Instructor</th>
            {showPrepStatus && <th className={thClass}>Prep call</th>}
            <th className={thClass} />
          </tr>
        </thead>
        <tbody>
          {list.items.map((c) => (
            <tr key={c.id}>
              <td className={`${tdClass} whitespace-nowrap`}>
                {c.start?.slice(0, 10) ?? '—'}
              </td>
              <td
                className={`${tdClass} whitespace-nowrap text-right text-gray-three`}
              >
                {c.daysOut == null
                  ? '—'
                  : c.daysOut === 0
                  ? 'today'
                  : `${c.daysOut} d`}
              </td>
              <td className={`${tdClass} font-medium text-gray-four`}>
                <Link
                  to={classLink(c.id)}
                  className="hover:text-blue-primary hover:underline"
                >
                  {c.name ?? c.id}
                </Link>
                {c.classNumber && (
                  <span className="ml-2 text-xs text-gray-two">
                    #{c.classNumber}
                  </span>
                )}
                <span className="ml-2">
                  <OriginBadge local={c.localOrigin} />
                </span>
              </td>
              <td className={tdClass}>
                {c.classType?.replace(' Class', '') ?? '—'}
              </td>
              <td className={tdClass}>
                {c.accountId ? (
                  <Link
                    to={accountLink(c.accountId)}
                    className="hover:text-blue-primary hover:underline"
                  >
                    {c.accountName ?? c.accountId}
                  </Link>
                ) : (
                  <span className="text-gray-two">—</span>
                )}
              </td>
              <td className={tdClass}>{c.instructorName ?? '—'}</td>
              {showPrepStatus && (
                <td className={tdClass}>
                  <Pill
                    value={(c as { prepCallStatus?: string }).prepCallStatus}
                    tone="neutral"
                  />
                </td>
              )}
              <td className={`${tdClass} text-right`}>
                {fixTo ? (
                  <Link to={fixTo} className={btnGhost}>
                    {fixLabel}
                  </Link>
                ) : (
                  <Link to={classLink(c.id)} className={btnGhost}>
                    Open
                  </Link>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </ListCard>
);

// ── Programs (reviews due, wraps due) ──────────────────────────────────────

const ProgramList: React.FC<{
  id: ListKey;
  meta: { title: string; replaces: string };
  list: {
    count: number;
    basis: string;
    items: (TExceptionProgram | TExceptionReviewDue)[];
  };
  kind: TReviewKind;
  onRecord: (b: TAccountReviewCreate) => void;
  saving: boolean;
}> = ({ id, meta, list, kind, onRecord, saving }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const isReview = kind === 'review';
  return (
    <ListCard
      id={id}
      meta={meta}
      count={list.count}
      cap={200}
      basis={list.basis}
    >
      <div className="overflow-x-auto">
        <table className={tableClass}>
          <thead className={theadClass}>
            <tr>
              <th className={thClass}>Account</th>
              <th className={thClass}>Program</th>
              <th className={thClass}>Status</th>
              <th className={thClass}>Ends</th>
              <th className={`${thClass} text-right`}>
                {isReview ? 'Last review' : 'In'}
              </th>
              <th className={`${thClass} text-right`}>Remaining</th>
              <th className={thClass} />
            </tr>
          </thead>
          <tbody>
            {list.items.map((p) => {
              const r = p as TExceptionReviewDue;
              const open = openId === p.id;
              return (
                <React.Fragment key={p.id}>
                  <tr>
                    <td className={`${tdClass} font-medium text-gray-four`}>
                      {p.accountId ? (
                        <Link
                          to={accountLink(p.accountId)}
                          className="hover:text-blue-primary hover:underline"
                        >
                          {p.accountName ?? p.accountId}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={`${tdClass} max-w-xs truncate`}>
                      <Link
                        to={programLink(p.programId)}
                        className="hover:text-blue-primary hover:underline"
                      >
                        {p.contractNumber ?? p.programName ?? p.programId}
                      </Link>
                    </td>
                    <td className={tdClass}>
                      <Pill value={p.programStatus} />
                    </td>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {p.endDate ?? '—'}
                    </td>
                    <td
                      className={`${tdClass} whitespace-nowrap text-right text-gray-three`}
                    >
                      {isReview
                        ? r.lastReviewAt
                          ? `${r.daysSinceReview} d ago`
                          : 'never'
                        : p.daysToEnd == null
                        ? '—'
                        : `${p.daysToEnd} d`}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {money(p.remainingBalance)}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      <button
                        className={open ? btnSecondary : btnGhost}
                        onClick={() => setOpenId(open ? null : p.id)}
                      >
                        {open
                          ? 'Cancel'
                          : isReview
                          ? 'Record review'
                          : 'Record wrap'}
                      </button>
                    </td>
                  </tr>
                  {open && p.accountId && (
                    <tr>
                      <td className={`${tdClass} bg-grey-50`} colSpan={7}>
                        <RecordForm
                          kind={kind}
                          saving={saving}
                          onSubmit={(notes, nextAt) => {
                            onRecord({
                              accountId: p.accountId!,
                              programId: p.programId,
                              kind,
                              notes: notes || undefined,
                              nextAt: nextAt || undefined,
                            });
                            setOpenId(null);
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </ListCard>
  );
};

// ── Watchlist ──────────────────────────────────────────────────────────────

const WatchList: React.FC<{
  id: ListKey;
  meta: { title: string; replaces: string };
  list: { count: number; basis: string; items: TExceptionWatch[] };
  onRecord: (b: TAccountReviewCreate) => void;
  saving: boolean;
}> = ({ id, meta, list, onRecord, saving }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <ListCard
      id={id}
      meta={meta}
      count={list.count}
      cap={200}
      basis={list.basis}
    >
      <div className="overflow-x-auto">
        <table className={tableClass}>
          <thead className={theadClass}>
            <tr>
              <th className={thClass}>Account</th>
              <th className={thClass}>Last session</th>
              <th className={`${thClass} text-right`}>Days since</th>
              <th className={`${thClass} text-right`}>Programs</th>
              <th className={thClass}>Next program end</th>
              <th className={`${thClass} text-right`}>Remaining</th>
              <th className={thClass} />
            </tr>
          </thead>
          <tbody>
            {list.items.map((w) => {
              const open = openId === w.id;
              return (
                <React.Fragment key={w.id}>
                  <tr>
                    <td className={`${tdClass} font-medium text-gray-four`}>
                      <Link
                        to={accountLink(w.accountId)}
                        className="hover:text-blue-primary hover:underline"
                      >
                        {w.accountName ?? w.accountId}
                      </Link>
                    </td>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {w.lastSessionAt?.slice(0, 10) ?? (
                        <span className="text-gray-three">none on record</span>
                      )}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {w.daysSinceLastSession ?? '—'}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {w.programIds.length === 1 ? (
                        <Link
                          to={programLink(w.programIds[0])}
                          className="hover:text-blue-primary hover:underline"
                        >
                          1
                        </Link>
                      ) : (
                        w.programs
                      )}
                    </td>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {w.nextProgramEnd ?? '—'}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {money(w.remainingBalance)}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      <button
                        className={open ? btnSecondary : btnGhost}
                        onClick={() => setOpenId(open ? null : w.id)}
                      >
                        {open ? 'Cancel' : 'Record review'}
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td className={`${tdClass} bg-grey-50`} colSpan={7}>
                        <RecordForm
                          kind="review"
                          saving={saving}
                          onSubmit={(notes, nextAt) => {
                            onRecord({
                              accountId: w.accountId,
                              programId:
                                w.programIds.length === 1
                                  ? w.programIds[0]
                                  : undefined,
                              kind: 'review',
                              notes: notes || undefined,
                              nextAt: nextAt || undefined,
                            });
                            setOpenId(null);
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </ListCard>
  );
};

// ── The inline record form ─────────────────────────────────────────────────

const RecordForm: React.FC<{
  kind: TReviewKind;
  saving: boolean;
  onSubmit: (notes: string, nextAt: string) => void;
}> = ({ kind, saving, onSubmit }) => {
  const [notes, setNotes] = useState('');
  const [nextAt, setNextAt] = useState('');
  return (
    <form
      className="flex flex-wrap items-end gap-3 py-1"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(notes.trim(), nextAt ? new Date(nextAt).toISOString() : '');
      }}
    >
      <div className="min-w-[280px] flex-1">
        <div className={labelClass}>Notes (optional)</div>
        <input
          className={`${inputClass} w-full`}
          placeholder={
            kind === 'wrap'
              ? 'What was covered in the wrap…'
              : 'What was covered, what was agreed…'
          }
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      <div>
        <div className={labelClass}>Next {kind} (optional)</div>
        <input
          type="date"
          className={inputClass}
          value={nextAt}
          onChange={(e) => setNextAt(e.target.value)}
        />
      </div>
      <button type="submit" className={btnPrimary} disabled={saving}>
        {saving ? 'Saving…' : kind === 'wrap' ? 'Record wrap' : 'Record review'}
      </button>
    </form>
  );
};

export type { TReportExceptions };
export default Exceptions;
